import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EntityType, type Hcphco } from '@/core/graphql/generated/types';

import { useFieldChangeSubmit } from '../_hooks/useFieldChangeSubmit';
import { ConfirmSubmitModal } from '../ConfirmSubmitModal/ConfirmSubmitModal';
import {
	useInlineEditContext,
	type DraftFieldChange
} from '../InlineEditContext';

import { nameEditFormStyles as sx } from './NameEditForm.styles';

const ACTIVE_KEY = '__name__';

type FieldDef = {
	name: string;
	label: string;
	required: boolean;
};

const getFieldDefs = (
	entityType: EntityType,
	t: (k: string) => string
): FieldDef[] =>
	entityType === EntityType.Hcp
		? [
				{ name: 'firstNameCda_V', label: t('First Name'), required: true },
				{ name: 'middleNameCda_V', label: t('Middle Name'), required: false },
				{ name: 'lastNameCda_V', label: t('Last Name'), required: true }
			]
		: [{ name: 'hcoNameCda_V', label: t('Name'), required: true }];

const getInitialValues = (profile: Hcphco): Record<string, string> => {
	if (profile.__typename === 'HCP') {
		return {
			firstNameCda_V: profile.firstNameCda_V ?? '',
			middleNameCda_V: profile.middleNameCda_V ?? '',
			lastNameCda_V: profile.lastNameCda_V ?? ''
		};
	}

	if (profile.__typename === 'HCO') {
		return {
			hcoNameCda_V: profile.hcoNameCda_V ?? ''
		};
	}

	return {};
};

type FormProps = {
	profile: Hcphco;
	onClose: () => void;
};

export const NameEditForm = ({ profile, onClose }: FormProps) => {
	const { t } = useTranslation();
	const ctx = useInlineEditContext();

	const entityType = ctx?.entityType ?? EntityType.Hcp;
	const fieldDefs = useMemo(() => getFieldDefs(entityType, t), [entityType, t]);
	const initialValues = useMemo(() => getInitialValues(profile), [profile]);

	const [values, setValues] = useState<Record<string, string>>(initialValues);

	const submitChanges = useCallback(
		(
			changes: DraftFieldChange[],
			notes: string | undefined,
			thirdPartyConfirmed: boolean,
			attachmentIds: string[]
		) => {
			if (!ctx) return Promise.resolve({ ok: false as const, error: '' });

			return ctx.submitFields({
				changes,
				notes,
				thirdPartyConfirmed,
				attachmentIds
			});
		},
		[ctx]
	);

	const {
		pendingChanges,
		setPendingChanges,
		submitting,
		cancelSubmit,
		confirmSubmit
	} = useFieldChangeSubmit(ACTIVE_KEY, submitChanges, onClose);

	const dirtyFieldNames = useMemo(
		() =>
			fieldDefs
				.map(f => f.name)
				.filter(
					name =>
						(values[name] ?? '').trim() !== (initialValues[name] ?? '').trim()
				),
		[fieldDefs, values, initialValues]
	);
	const isDirty = dirtyFieldNames.length > 0;
	const requiredMissing = fieldDefs.some(
		f => f.required && (values[f.name] ?? '').trim() === ''
	);
	const canSubmit = isDirty && !requiredMissing;

	useEffect(() => {
		ctx?.setActiveDirty(isDirty);
	}, [ctx, isDirty]);

	useEffect(() => {
		ctx?.requestActivate(ACTIVE_KEY);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const isStillActive = ctx?.activeFieldName === ACTIVE_KEY;
	const becameActiveRef = useRef(false);
	useEffect(() => {
		if (isStillActive) {
			becameActiveRef.current = true;
			return;
		}

		if (becameActiveRef.current && !pendingChanges) {
			onClose();
		}
	}, [isStillActive, pendingChanges, onClose]);

	const handleClose = useCallback(() => {
		if (pendingChanges) return;

		ctx?.requestCloseActive();
	}, [ctx, pendingChanges]);

	const handleSubmit = useCallback(() => {
		if (!canSubmit) return;

		const changes: DraftFieldChange[] = dirtyFieldNames.map(name => {
			const raw = (values[name] ?? '').trim();
			return {
				fieldName: name,
				inputType: 'text',
				value: raw === '' ? null : raw,
				displayValue: raw === '' ? null : raw,
				isNewField: (initialValues[name] ?? '').trim() === ''
			};
		});
		setPendingChanges(changes);
	}, [canSubmit, dirtyFieldNames, values, initialValues, setPendingChanges]);

	const fieldLabelByName = useMemo(() => {
		const map: Record<string, string> = {};
		for (const f of fieldDefs) map[f.name] = f.label;
		return map;
	}, [fieldDefs]);

	const confirmEntries = pendingChanges
		? pendingChanges.map(c => ({
				label: fieldLabelByName[c.fieldName] ?? c.fieldName,
				previousDisplay:
					(initialValues[c.fieldName] ?? '').trim() === ''
						? null
						: initialValues[c.fieldName],
				newDisplay: typeof c.displayValue === 'string' ? c.displayValue : '',
				isNewField: !!c.isNewField
			}))
		: [];

	return (
		<>
			<Stack spacing={1.5}>
				<Typography variant='subtitle2' sx={sx.title}>
					{t('Edit Name')}
				</Typography>
				{fieldDefs.map(f => {
					const hadInitial = (initialValues[f.name] ?? '').trim() !== '';
					const isMissing =
						f.required && (values[f.name] ?? '').trim() === '' && hadInitial;
					return (
						<TextField
							key={f.name}
							label={f.label}
							value={values[f.name] ?? ''}
							onChange={e =>
								setValues(prev => ({ ...prev, [f.name]: e.target.value }))
							}
							required={f.required}
							disabled={submitting}
							helperText={isMissing ? t('This field is required') : undefined}
							error={isMissing}
							size='small'
							fullWidth
						/>
					);
				})}

				<Box sx={sx.actions}>
					<Button
						variant='outlined'
						size='small'
						onClick={handleClose}
						disabled={submitting}
					>
						{t('Cancel')}
					</Button>
					<Button
						variant='contained'
						size='small'
						onClick={handleSubmit}
						disabled={submitting || !canSubmit}
						sx={sx.submitButtonDisabled}
					>
						{t('Submit')}
					</Button>
				</Box>
			</Stack>

			<ConfirmSubmitModal
				open={!!pendingChanges}
				changes={confirmEntries}
				submitting={submitting}
				onCancel={cancelSubmit}
				onConfirm={confirmSubmit}
			/>
		</>
	);
};
